import { useCallback } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import type { RectanglePoint } from './types.js';
import { useCanvas } from '../../../../core/components/canvas/hooks/useCanvas.js';
import { getCanvasColor } from '../../../../core/components/canvas/utils/canvas-color.js';
import { DEFAULT_HIGHLIGHTED_OPACITY } from '../../../../core/constants.js';
import { MIN_BAR_HIGHLIGHT_SIZE_PX } from '../../../constants/defaults.js';
import { BAR_ID } from '../../../types/xy-chart-internals.js';

interface HighlightedRectangleCanvasProps {
  points: RectanglePoint[];
}

const OUTER_OFFSET = 2;
const OUTER_SHRINK = 3;
const INNER_OFFSET = 3;
const INNER_SHRINK = 5;
const BORDER_WIDTH = 1;

const getMinSizeHighlightBar = (
  point: RectanglePoint,
  canvasWidth: number,
  canvasHeight: number,
) => {
  const width = Math.max(point.width, MIN_BAR_HIGHLIGHT_SIZE_PX);
  const height = Math.max(point.height, MIN_BAR_HIGHLIGHT_SIZE_PX);
  const xExpansion = (width - point.width) / 2;
  const yExpansion = (height - point.height) / 2;

  const rawX = point.x - xExpansion;
  const clampedX = Math.max(0, rawX);
  const clampedWidth = Math.max(
    0,
    Math.min(rawX + width, canvasWidth) - clampedX,
  );

  return {
    x: clampedX,
    y: Math.max(0, Math.min(point.y - yExpansion, canvasHeight - height)),
    width: clampedWidth,
    height,
  };
};

const getCanvasCssSize = (ctx: CanvasRenderingContext2D) => {
  const dpr = window.devicePixelRatio || 1;
  return {
    width: ctx.canvas.width / dpr,
    height: ctx.canvas.height / dpr,
  };
};

export const HighlightedRectangleCanvas = ({
  points,
}: HighlightedRectangleCanvasProps) => {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const { width: canvasWidth, height: canvasHeight } =
        getCanvasCssSize(ctx);

      for (const point of points) {
        const { x, y, width, height } =
          point.id === BAR_ID
            ? getMinSizeHighlightBar(point, canvasWidth, canvasHeight)
            : point;

        // Outer border
        ctx.beginPath();
        ctx.lineWidth = BORDER_WIDTH;
        ctx.strokeStyle = getCanvasColor(Colors.Text.Neutral.Default);
        ctx.rect(
          x + OUTER_OFFSET,
          y + OUTER_OFFSET,
          Math.max(0, width - OUTER_SHRINK),
          Math.max(0, height - OUTER_SHRINK),
        );
        ctx.stroke();
        ctx.closePath();

        // Calculate inner rectangle dimensions
        const innerX = x + INNER_OFFSET;
        const innerY = y + INNER_OFFSET;
        const innerWidth = width - INNER_SHRINK;
        const innerHeight = height - INNER_SHRINK;

        // Only draw inner border if it has positive size
        if (innerWidth > 0 && innerHeight > 0) {
          ctx.beginPath();
          ctx.lineWidth = BORDER_WIDTH;
          ctx.globalAlpha = DEFAULT_HIGHLIGHTED_OPACITY;
          ctx.strokeStyle = getCanvasColor(Colors.Background.Base.Default);
          ctx.rect(innerX, innerY, innerWidth, innerHeight);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.closePath();
        }
      }

      return false;
    },
    [points],
  );

  useCanvas('Highlighted-rects', draw);

  return null;
};
